import React, {useEffect, useMemo, useState} from "react";
import {
    CardCvcElement,
    CardExpiryElement,
    CardNumberElement,
    useElements,
    useStripe
} from "@stripe/react-stripe-js";
import {TextField} from "@material-ui/core";
import Utils from "../../../../helpers/Utils";
import useResponsiveFontSize from "../../../../hooks/useResponsiveFontSize";
import {addBalanceModalStyle} from "../AddBalanceModalStyle";
import {userCardSave} from "../../../../api/billingApi";
import BasicSelect from "../../../Common/BasicSelect";

const useOptions = () => {
    const fontSize = useResponsiveFontSize();

    return useMemo(
        () => ({
            cardNumberOption: {
                style: {
                    base: {
                        fontSize,
                        color: '#36454f',
                        letterSpacing: '0.025em',
                        fontFamily: 'Source Code Pro, monospace',
                        '::placeholder': {
                            color: '#929da9'
                        }
                    },
                    invalid: {
                        color: '#9e2146'
                    },
                },
                showIcon: true
            },
            cardExpiryOption: {
                style: {
                    base: {
                        fontSize,
                        color: '#36454f',
                        letterSpacing: '0.025em',
                        fontFamily: 'Source Code Pro, monospace',
                        '::placeholder': {
                            color: '#929da9'
                        }
                    },
                    invalid: {
                        color: '#9e2146'
                    },
                },
            },
            cardCvcOption: {
                style: {
                    base: {
                        fontSize,
                        color: '#36454f',
                        letterSpacing: '0.025em',
                        fontFamily: 'Source Code Pro, monospace',
                        '::placeholder': {
                            color: '#929da9'
                        }
                    },
                    invalid: {
                        color: '#9e2146'
                    },
                },
            }
        }),
        [fontSize]
    );
};

const AddCardForm = ({ onSuccess, handleClose }) => {
    const [submitting, setSubmitting] = useState(false);
    const classes = addBalanceModalStyle({ disabled: submitting });
    const stripe = useStripe();
    const elements = useElements();
    const { cardNumberOption, cardExpiryOption, cardCvcOption } = useOptions();
    const [email, setEmail] = useState('');
    const [country, setCountry] = useState('US');
    const [zip, setZip] = useState('');
    const [stripeErrorMessage, setStripeErrorMessage] = useState({
        cardNumber: '',
        cardExpiry: '',
        cardCvc: '',
        zipCode: ''
    });

    useEffect(() => {
        if (Utils.getAccountData('email')) {
            let tempEmail = Utils.getAccountData('email').toLowerCase();
            setEmail(tempEmail);
        }
    }, [])

    const handleStripeError = (field, event) => {
        if (event.error){
            setStripeErrorMessage((prevState)=>({ ...prevState, [field]: event.error.message }));
        }else {
            setStripeErrorMessage(prevState => ({ ...prevState, [field]: '' }));
        }
    };

    const handleSubmitChange = async () => {
        const cardNumberElement = elements.getElement(CardNumberElement);

        if (!stripe || !elements || !cardNumberElement) return;

        setSubmitting(true);
        const {paymentMethod, error} = await stripe.createPaymentMethod({
            type: 'card',
            card: cardNumberElement,
            billing_details: {
                name: Utils.getAccountData('fullName'),
                email: email,
                address: {
                    country: country,
                    postal_code: zip,
                }
            },
            metadata: {
                user_id: Utils.getAccountData('userId'),
                agency_id: Utils.getAccountData('agencyId'),
                payment_authority: 'salespype'
            },
        });

        if (error){
            if (error.code.includes('number')) {
                handleStripeError('cardNumber', { error });
            } else if (error.code.includes('expiry')) {
                handleStripeError('cardExpiry', { error });
            } else if (error.code.includes('cvc')) {
                handleStripeError('cardCvc', { error });
            }else if (error.code.includes('card_declined')){
                handleStripeError('cardNumber', { error });
            }else {
                window.showNotification('error', error?.message ?? 'Something went wrong!');
            }
            setSubmitting(false);
        }

        if (zip.length === 0){
            setStripeErrorMessage((prevState)=>({ ...prevState, zipCode: 'Your ZIP code is incomplete.' }));
            setSubmitting(false);
            return;
        }

        if (zip.length > 0 && !Utils.isValidUSZip(zip)){
            setStripeErrorMessage((prevState)=>({ ...prevState, zipCode: 'Invalid ZIP code.' }));
            setSubmitting(false);
            return;
        }

        if (!error && paymentMethod){
            try{
                const resp = await userCardSave({
                    payment_method_id: paymentMethod.id,
                    card_details: {
                        brand: paymentMethod.card.brand,
                        country: paymentMethod.card.country,
                        display_brand: paymentMethod.card.display_brand,
                        exp_month: paymentMethod.card.exp_month,
                        exp_year: paymentMethod.card.exp_year,
                        last4: paymentMethod.card.last4
                    }
                });

                if (resp?.success){
                    onSuccess();
                    window.showNotification('success', resp.message || 'Card added successfully');
                }else {
                    window.showNotification('success', resp.message || 'Failed to add card. Please contact with system admin');
                }
            }catch (err){
                console.log(err);
                window.showNotification('error', 'Something went wrong!');
            }finally {
                setSubmitting(false);
            }
        }
    };

    return(
        <>
            <div className={classes.modalBody} style={{ minHeight: 200, position: 'relative' }}>
                <div className={classes.cardContainer}>
                    <label>
                        Email
                        <TextField
                            variant="outlined"
                            value={email}
                            disabled
                            onChange={(e)=>{}}
                            className={classes.cardEmailField}
                        />
                    </label>

                    <label>
                        Card Number
                        <CardNumberElement
                            options={{
                                ...cardNumberOption,
                                disabled: submitting
                            }}
                            onReady={() => {
                                console.log("CardNumberElement [ready]");
                            }}
                            onChange={(event)=>{
                                handleStripeError('cardNumber', event);
                            }}
                        />
                        {
                            stripeErrorMessage.cardNumber &&
                            <span className={classes.stripeErrorMessage}>{stripeErrorMessage.cardNumber}</span>
                        }
                    </label>

                    <div className={classes.cardElemFlex}>
                        <label>
                            Expiration Date
                            <CardExpiryElement
                                options={{
                                    ...cardExpiryOption,
                                    disabled: submitting
                                }}
                                onReady={() => {
                                    console.log("CardExpiryElement [ready]");
                                }}
                                onChange={(event)=>{
                                    handleStripeError('cardExpiry', event);
                                }}
                            />
                            {
                                stripeErrorMessage.cardExpiry &&
                                <span className={classes.stripeErrorMessage}>{stripeErrorMessage.cardExpiry}</span>
                            }
                        </label>

                        <label>
                            Security Code
                            <CardCvcElement
                                options={{
                                    ...cardCvcOption,
                                    disabled: submitting,
                                }}
                                onReady={() => {
                                    console.log("CardCvcElement [ready]");
                                }}
                                onChange={(event)=>{
                                    handleStripeError('cardCvc', event);
                                }}
                            />
                            {
                                stripeErrorMessage.cardCvc &&
                                <span className={classes.stripeErrorMessage}>{stripeErrorMessage.cardCvc}</span>
                            }
                        </label>
                    </div>

                    <div className={classes.cardElemFlex}>
                        <label>
                            Country
                            <BasicSelect
                                fullWidth
                                options={[
                                    {
                                        title: 'United States',
                                        id: 'US'
                                    }
                                ]}
                                value={country}
                                onChange={(e) => {
                                    setCountry(e.target.value);
                                }}
                                className={classes.cardCountrySelect}
                                disabled={submitting}
                            />
                        </label>
                        <label>
                            Zip Code
                            <TextField
                                variant="outlined"
                                value={zip}
                                placeholder="12345"
                                onChange={(e) => {
                                    const input = e.target.value;
                                    if (/^[0-9-]*$/.test(input)) {
                                        setZip(input);
                                    }

                                    if (stripeErrorMessage.zipCode){
                                        setStripeErrorMessage((prevState)=>({ ...prevState, zipCode: '' }));
                                    }
                                }}
                                error={(zip.length > 0 && !Utils.isValidUSZip(zip)) || !!stripeErrorMessage.zipCode}
                                helperText={stripeErrorMessage.zipCode ?
                                    stripeErrorMessage.zipCode :
                                    (zip.length > 0 && !Utils.isValidUSZip(zip)
                                    ? "Invalid ZIP code"
                                    : " ")}
                                className={classes.cardZipField}
                                disabled={submitting}
                            />

                        </label>
                    </div>
                </div>
            </div>

            <div className={classes.modalFooter}>
                <div
                    className={classes.actionContainer}
                    style={{ display: "flex", alignItems: "center", gap: 16 }}
                >
                    <button
                        onClick={handleClose}
                        className={`${classes.button} ${classes.closeButton}`}
                        style={{ padding: "8px 24px" }}
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSubmitChange}
                        className={`${classes.button} ${classes.confirmButton} ${
                            submitting ? classes.disabled : ""
                        }`}
                        disabled={submitting}
                    >
                        {submitting ? "Adding" : "Add"}
                    </button>
                </div>
            </div>
        </>
    )
};

export default AddCardForm;